import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { useMemo } from "react";
import { FILE_GRID_GAP_PX, FILE_GRID_TEMPLATE_COLUMNS } from "../fileGrid";

/** One shimmering placeholder block. */
function Shimmer({
  w,
  h,
  radius = 6,
  circle = false,
}: {
  w: number | string;
  h: number | string;
  radius?: number;
  circle?: boolean;
}) {
  return (
    <Box
      className="skeleton-shimmer"
      sx={{
        width: w,
        height: h,
        // Never exceed the container, so a fixed-width bar can't overflow a
        // narrow (mobile) header box.
        maxWidth: "100%",
        borderRadius: circle ? "50%" : `${radius}px`,
        flex: "none",
      }}
    />
  );
}

/**
 * Loading skeleton for the desktop published-space view. Mirrors
 * {@link DesktopSpaceBrowser}'s layout pixel-for-pixel — identical outer
 * padding, header card, toolbar, and either a card grid or a table-style list
 * (per `view`) using the same geometry — so the transition to the real content
 * lands every element in place with no layout shift. The `view` matches the
 * user's persisted layout so the skeleton always resembles what's loading.
 */
export function DesktopSpaceSkeleton({
  view = "grid",
}: {
  view?: "grid" | "list";
}) {
  // Match the real view's padding threshold (DesktopSpaceBrowser switches to the
  // compact 20px gutters at <= 920px, not at the MUI `sm` breakpoint), so the
  // skeleton has the same side padding and doesn't over-inset on tablet widths.
  const compact = useMemo(() => window.innerWidth <= 920, []);

  // Estimate how many cards fill the viewport under the shared grid template so
  // the skeleton covers the fold (extra items below simply fade out).
  const cardCount = useMemo(() => {
    const gutters = compact ? 40 : 220;
    const avail = Math.max(0, window.innerWidth - gutters);
    const cols = Math.max(2, Math.floor(avail / (174 + FILE_GRID_GAP_PX)));
    const colW = (avail - (cols - 1) * FILE_GRID_GAP_PX) / cols;
    const cardH = colW * (204 / 174);
    const rows = Math.max(
      2,
      Math.ceil((window.innerHeight - 300) / (cardH + FILE_GRID_GAP_PX)) + 1
    );
    return cols * rows;
  }, [compact]);
  // Rows that fill the fold for the list layout (52px each).
  const rowCount = useMemo(
    () => Math.max(4, Math.ceil((window.innerHeight - 300) / 52) + 1),
    []
  );

  return (
    <Box
      aria-hidden
      sx={{
        px: compact ? "20px" : "110px",
        pt: compact ? 3 : 5,
        pb: 14,
      }}
    >
      {/* Header card — same Paper geometry as SpaceHeader (incl. the reserved
          download-button column on the right). */}
      <Paper
        elevation={0}
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: { xs: 2, sm: 3 },
          p: { xs: 2.5, sm: 3.5 },
          pr: "72px",
          borderRadius: "14px",
          border: "1px solid rgba(218,218,218,0.5)",
          backgroundColor: "#ffffff",
        }}
      >
        <Box
          className="skeleton-shimmer"
          sx={{
            width: { xs: 72, sm: 92 },
            height: { xs: 72, sm: 92 },
            borderRadius: "50%",
            flex: "none",
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Shimmer w={280} h={20} radius={5} />
          <Box sx={{ mt: 0.75 }}>
            <Shimmer w={190} h={12} radius={4} />
          </Box>
          <Stack
            direction="row"
            flexWrap="wrap"
            sx={{ mt: 1.75, columnGap: 2, rowGap: 1 }}
          >
            <Shimmer w={104} h={12} radius={6} />
            <Shimmer w={70} h={12} radius={6} />
            <Shimmer w={140} h={12} radius={6} />
          </Stack>
        </Box>
        {/* Download button placeholder — top-right, mirroring the real header. */}
        <Box
          className="skeleton-shimmer"
          sx={{
            position: "absolute",
            top: 20,
            right: 20,
            width: 24,
            height: 24,
            borderRadius: "50%",
          }}
        />
      </Paper>

      {/* Toolbar — breadcrumb (home + name) left, view toggle right. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 3, mb: 2 }}>
        <Box
          sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}
        >
          <Shimmer w={30} h={30} circle />
          <Box sx={{ ml: "15px" }}>
            <Shimmer w={130} h={15} radius={4} />
          </Box>
        </Box>
        <Shimmer w={80} h={30} radius={15} />
      </Box>

      {view === "list" ? (
        <>
          {/* Column-label row (Type / Name / Size / Last modified). */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              px: "12px",
              pb: 1,
            }}
          >
            <Box sx={{ width: 22, flex: "none" }} />
            <Shimmer w={28} h={11} radius={4} />
            <Shimmer w={40} h={11} radius={4} />
            <Box sx={{ ml: "auto", display: "flex", gap: 2 }}>
              <Shimmer w={28} h={11} radius={4} />
              <Shimmer w={80} h={11} radius={4} />
            </Box>
          </Box>
          {/* Row placeholders — mirror the 52px row geometry. */}
          {Array.from({ length: rowCount }, (_, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                px: "12px",
                height: 52,
              }}
            >
              <Shimmer w={18} h={18} radius={3} />
              <Shimmer w={26} h={26} circle />
              <Shimmer w={`${28 + ((i * 7) % 20)}%`} h={14} radius={5} />
              <Box
                sx={{
                  ml: "auto",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Shimmer w={64} h={13} radius={5} />
                <Shimmer w={88} h={13} radius={5} />
                <Box sx={{ width: 60, flex: "none" }} />
              </Box>
            </Box>
          ))}
        </>
      ) : (
        /* Card grid — identical template + gap to the real grid. */
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: FILE_GRID_TEMPLATE_COLUMNS,
            gap: `${FILE_GRID_GAP_PX}px`,
          }}
        >
          {Array.from({ length: cardCount }, (_, i) => (
            <Box
              key={i}
              className="skeleton-shimmer"
              sx={{
                aspectRatio: "174 / 204",
                borderRadius: "6px",
                width: "100%",
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
